import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Bluetooth, BluetoothConnected, BluetoothSearching } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { useFeature } from '../../context/FeaturesContext';
import { connectScale, isBluetoothSupported } from '../../lib/bleScale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Composición corporal avanzada capturada en el checkpoint. Refleja los mismos
// parámetros del cuestionario inicial para dar continuidad al historial de
// biometría. Solo se muestran a usuarios cuyo plan incluye biometrics_advanced.
const ADVANCED_METRICS = [
  { key: 'muscle_mass_kg', label: 'Masa muscular', unit: 'kg' },
  { key: 'water_pct', label: 'Agua corporal', unit: '%' },
  { key: 'visceral_fat', label: 'Grasa visceral', unit: 'nivel' },
  { key: 'bone_mass_kg', label: 'Masa ósea', unit: 'kg' },
  { key: 'protein_pct', label: 'Proteína', unit: '%' },
  { key: 'metabolic_age', label: 'Edad metabólica', unit: 'años' },
];

/**
 * CheckpointDialog — short form the user fills at the end of a 4-week block
 * to advance to the next block of the macroplan.
 *
 * Además del peso/grasa y el estado subjetivo del bloque, ahora captura la
 * composición corporal completa (masa muscular, agua, grasa visceral, masa
 * ósea, proteína, edad metabólica) para quienes su plan lo permite. Estos
 * datos se registran como una medición biométrica real. Se pueden llenar a
 * mano o traerlos desde una báscula inteligente (/app/devices).
 */
// Campos del formulario que corresponden a métricas biométricas. La báscula
// solo puede llenar estos; los faltantes se cargan a mano.
const FORM_METRIC_FIELDS = new Set(['weight_kg', 'body_fat_pct']);

const CheckpointDialog = ({ open, onOpenChange, currentBlockNumber, onSuccess }) => {
  const { enabled: advancedEnabled } = useFeature('biometrics_advanced');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    weight_kg: '',
    body_fat_pct: '',
    adherence_pct: 80,
    energy_level: 7,
    pain_or_injury: false,
    pain_notes: '',
    notes: '',
  });
  // Métricas avanzadas: mapa metric_key -> string del input.
  const [advMetrics, setAdvMetrics] = useState({});
  // Estado de la báscula BLE.
  const [scaleStatus, setScaleStatus] = useState('idle'); // idle | connecting | connected

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const updateAdv = (key, value) =>
    setAdvMetrics((m) => {
      if (value === '') {
        const next = { ...m };
        delete next[key];
        return next;
      }
      return { ...m, [key]: value };
    });

  // Vuelca en el formulario solo las métricas necesarias que trajo la báscula.
  // Lo que no venga queda intacto para carga manual; lo extra ya viene filtrado.
  const applyScaleMetrics = (metrics) => {
    if (!metrics || Object.keys(metrics).length === 0) return;
    let filled = 0;
    Object.entries(metrics).forEach(([key, value]) => {
      if (value == null) return;
      if (FORM_METRIC_FIELDS.has(key)) {
        update(key, String(value));
        filled += 1;
      } else {
        // Métrica avanzada (ej. masa muscular): respeta el gating por plan.
        if (advancedEnabled) {
          updateAdv(key, String(value));
          filled += 1;
        }
      }
    });
    if (filled > 0) {
      toast.success('Medición recibida de la báscula');
    }
  };

  const handleConnectScale = async () => {
    if (!isBluetoothSupported()) {
      toast.error('Tu navegador no soporta Web Bluetooth. Usa Chrome/Edge en desktop o Android.');
      return;
    }
    setScaleStatus('connecting');
    try {
      await connectScale({
        onMetrics: applyScaleMetrics,
        onDisconnect: () => setScaleStatus('idle'),
      });
      setScaleStatus('connected');
      toast.info('Báscula conectada. Súbete para medir.');
    } catch (err) {
      setScaleStatus('idle');
      if (err?.name === 'NotFoundError') {
        toast.info('Búsqueda de báscula cancelada');
      } else if (err?.code !== 'NO_BLUETOOTH') {
        toast.error('No se pudo conectar la báscula');
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.weight_kg) {
      toast.error('Ingresa tu peso actual');
      return;
    }
    setSubmitting(true);
    try {
      // Construye el bloque de métricas biométricas solo con lo capturado.
      let metrics = null;
      if (advancedEnabled) {
        const entries = Object.entries(advMetrics)
          .map(([k, v]) => [k, parseFloat(v)])
          .filter(([, v]) => !Number.isNaN(v));
        if (entries.length > 0) metrics = Object.fromEntries(entries);
      }

      const res = await axios.post(`${API}/methodology/me/macroplan/checkpoint`, {
        weight_kg: parseFloat(form.weight_kg),
        body_fat_pct: form.body_fat_pct ? parseFloat(form.body_fat_pct) : null,
        adherence_pct: parseInt(form.adherence_pct),
        energy_level: parseInt(form.energy_level),
        pain_or_injury: form.pain_or_injury,
        pain_notes: form.pain_or_injury ? form.pain_notes : null,
        notes: form.notes || null,
        metrics,
      });
      if (res.data.status === 'macroplan_completed') {
        toast.success('Completaste tu macroplan.');
      } else {
        toast.success(`Bloque ${res.data.next_block.number} comenzado`);
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar el checkpoint');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-teko text-2xl text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            CHECKPOINT — BLOQUE {currentBlockNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-zinc-400 text-sm">
            Cuéntanos cómo te fue en estas 4 semanas. ONOR Coach usa esta información
            para diseñar tu siguiente bloque.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Peso actual (kg) *</Label>
              <Input
                type="number"
                step="0.1"
                className="input-default mt-1"
                value={form.weight_kg}
                onChange={(e) => update('weight_kg', e.target.value)}
                data-testid="checkpoint-weight"
              />
            </div>
            <div>
              <Label className="text-zinc-300">% Grasa (opcional)</Label>
              <Input
                type="number"
                step="0.1"
                className="input-default mt-1"
                value={form.body_fat_pct}
                onChange={(e) => update('body_fat_pct', e.target.value)}
              />
            </div>
          </div>

          {/* Composición corporal completa — solo planes con biometría avanzada */}
          {advancedEnabled && (
            <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300 text-sm">
                  Composición corporal (opcional)
                </Label>
                <button
                  type="button"
                  onClick={handleConnectScale}
                  disabled={scaleStatus === 'connecting'}
                  className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary transition-colors disabled:opacity-50"
                  data-testid="checkpoint-connect-scale"
                >
                  {scaleStatus === 'connecting' ? (
                    <><BluetoothSearching className="w-3.5 h-3.5 animate-pulse" /> Buscando…</>
                  ) : scaleStatus === 'connected' ? (
                    <><BluetoothConnected className="w-3.5 h-3.5" /> Conectada</>
                  ) : (
                    <><Bluetooth className="w-3.5 h-3.5" /> Conectar báscula</>
                  )}
                </button>
              </div>
              <p className="text-zinc-500 text-xs">
                {scaleStatus === 'connected'
                  ? 'Súbete a la báscula: los datos que envíe llenarán los campos; el resto cárgalos a mano.'
                  : 'Cárgalos a mano o conecta tu báscula inteligente; solo llenará los parámetros que envíe.'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {ADVANCED_METRICS.map((m) => (
                  <div key={m.key}>
                    <Label className="text-zinc-400 text-xs">
                      {m.label} <span className="text-zinc-600">({m.unit})</span>
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="--"
                      className="input-default mt-1"
                      value={advMetrics[m.key] || ''}
                      onChange={(e) => updateAdv(m.key, e.target.value)}
                      data-testid={`checkpoint-${m.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-zinc-300">
              Adherencia al plan: <span className="text-primary">{form.adherence_pct}%</span>
            </Label>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={form.adherence_pct}
              onChange={(e) => update('adherence_pct', e.target.value)}
              className="w-full accent-primary mt-2"
            />
            <p className="text-zinc-500 text-xs">¿Qué tanto cumpliste tu plan de entrenamiento y nutrición?</p>
          </div>

          <div>
            <Label className="text-zinc-300">
              Nivel de energía: <span className="text-primary">{form.energy_level}/10</span>
            </Label>
            <input
              type="range"
              min="1"
              max="10"
              value={form.energy_level}
              onChange={(e) => update('energy_level', e.target.value)}
              className="w-full accent-primary mt-2"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-zinc-300">¿Tuviste alguna lesión o dolor?</Label>
            <Switch
              checked={form.pain_or_injury}
              onCheckedChange={(v) => update('pain_or_injury', v)}
            />
          </div>

          {form.pain_or_injury && (
            <div>
              <Label className="text-zinc-300">Describe la lesión / dolor</Label>
              <Textarea
                className="input-default mt-1"
                value={form.pain_notes}
                onChange={(e) => update('pain_notes', e.target.value)}
                placeholder="Ej: dolor en la rodilla derecha al hacer sentadilla profunda..."
              />
            </div>
          )}

          <div>
            <Label className="text-zinc-300">Comentarios (opcional)</Label>
            <Textarea
              className="input-default mt-1"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              placeholder="Cualquier comentario que quieras compartir..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} className="btn-primary" disabled={submitting}>
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</>
            ) : (
              'Generar siguiente bloque'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckpointDialog;
